import { useEffect } from "react"
import { AnimatePresence, motion } from "motion/react"
import { IconXmarkOutline18 as CloseIcon } from "nucleo-ui-outline-18"

import { Button, IconButton } from "@productlane/ui"

import { useShortcut } from "@/lib"
import { useEscapeClearsSelection } from "@/lib/hooks/use-escape-clears-selection"
import { useSelectionPillState } from "./selection-pill-state"

export interface SelectionPillAction {
	label: string
	onAction: () => void
	isLoading?: boolean
	/** Hotkeys in react-hotkeys-hook syntax, live only while rows are selected. */
	shortcut?: { keys: string; hint: string }
}

function ActionShortcut({
	keys,
	onAction,
	enabled,
}: {
	keys: string
	onAction: () => void
	enabled: boolean
}) {
	useShortcut(keys, () => onAction(), { enabled, preventDefault: true }, [
		onAction,
	])
	return null
}

interface Props {
	count: number
	noun: [singular: string, plural: string]
	onClear: () => void
	actions: SelectionPillAction[]
}

export function SelectionPill({ count, noun, onClear, actions }: Props) {
	// One bulk write at a time, so a second click cannot race the first.
	const isBusy = actions.some((action) => action.isLoading)
	const visible = count > 0
	useEscapeClearsSelection(visible, onClear)
	useEffect(() => {
		useSelectionPillState.setState({ visible })
		return () => useSelectionPillState.setState({ visible: false })
	}, [visible])

	return (
		<>
			{actions.map(
				(action) =>
					action.shortcut && (
						<ActionShortcut
							key={action.label}
							keys={action.shortcut.keys}
							onAction={action.onAction}
							enabled={visible && !isBusy}
						/>
					),
			)}
			<AnimatePresence>
				{count > 0 && (
					<motion.div
						className="pointer-events-none fixed inset-x-0 bottom-6 z-20 flex justify-center"
						initial={{ opacity: 0, transform: "translateY(8px)" }}
						animate={{ opacity: 1, transform: "translateY(0)" }}
						exit={{ opacity: 0, transform: "translateY(8px)" }}
						transition={{ duration: 0.15, ease: "easeOut" }}
					>
						<div className="pointer-events-auto flex items-center gap-2 rounded-full bg-white py-1.5 pl-4 pr-1.5 shadow-lg ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
							<span className="text-sm text-primary">
								{count} {count === 1 ? noun[0] : noun[1]} selected
							</span>
							{actions.map((action) => (
								<Button
									key={action.label}
									variant="outline"
									size="default"
									isLoading={action.isLoading}
									disabled={isBusy && !action.isLoading}
									command={action.shortcut?.hint}
									onClick={action.onAction}
								>
									{action.label}
								</Button>
							))}
							<IconButton
								variant="ghost"
								Icon={CloseIcon}
								aria-label="Clear selection"
								onClick={onClear}
							/>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	)
}
